import {
  createRouter,
  createWebHistory,
  type LocationQueryValue,
  type RouteRecordRaw,
} from "vue-router";
import type { PermissionLevel } from "../constants/enums/permission-level.enum";
import { storeToRefs } from "pinia";
import { useUserSettingsStore } from "../stores/user-settings";
import { useAuthStore } from "../stores/auth";
import { RouteName } from "./RouteNames";
import {
  cheminSansConnexionAutomatique,
  redirectionApresConnexion,
} from "../utils/follaw-depuis-mail";
import {
  agendaRoutes,
  alertingRoutes,
  amendmentsRoutes,
  articlesRoutes,
  cabinetMeetingRoutes,
  cabinetsRoutes,
  dashboardsRoutes,
  directRoutes,
  homeRoutes,
  JORFRoutes,
  legislativeRoutes,
  lobbiesRoutes,
  newsRoutes,
  nominationRoutes,
  onboardingRoutes,
  ordreDuJourRoutes,
  organesRoutes,
  parliamentaryOverviewRoutes,
  petitionsRoutes,
  PRMRoutes,
  questionsRoutes,
  reportsRoutes,
  settingsRoutes,
  socialMediaRoutes,
  stakeholdersRoutes,
  transcriptionsRoutes,
} from "./routes";
import { useUserStore } from "../stores/user";
import { useToast } from "vue-toastification";

//TODO
/**
 * 1 - Name routes
 * 2 - Type route declarations
 * 3 - Bring back side menu router to the main router file
 * 4 - Handle permission denied redirection via router guard
 * 5 - Switch router follawFR / FollawEU
 */

type SVRoute = RouteRecordRaw & {
  name: RouteName;
  meta?: {
    isPublic?: boolean;
    permissionLevel?: PermissionLevel;
    redirectPermissionDenied?: any;
  };
};

const routes: SVRoute[] = [
  {
    name: RouteName.Login,
    path: "/login",
    component: () => import("@follaw-fr/views/auth/LoginView.vue"),
    meta: {
      isPublic: true, // Allow access to even if not logged in
    },
  },
  {
    name: RouteName.Register,
    path: "/register",
    component: () => import("@follaw-fr/views/auth/RegisterView.vue"),
    meta: {
      isPublic: true, // Allow access to even if not logged in
    },
  },
  onboardingRoutes,

  {
    name: RouteName.Root,
    path: "/",
    component: () =>
      import("@follaw-fr/components/general/templates/AppStructure.vue"),
    redirect: { name: RouteName.Home },
    children: [
      agendaRoutes,
      alertingRoutes,
      amendmentsRoutes,
      articlesRoutes,
      cabinetMeetingRoutes,
      cabinetsRoutes,
      dashboardsRoutes,
      directRoutes,
      homeRoutes,
      JORFRoutes,
      legislativeRoutes,
      lobbiesRoutes,
      newsRoutes,
      nominationRoutes,
      ordreDuJourRoutes,
      organesRoutes,
      parliamentaryOverviewRoutes,
      petitionsRoutes,
      ...PRMRoutes,
      questionsRoutes,
      ...reportsRoutes,
      settingsRoutes,
      ...socialMediaRoutes,
      ...stakeholdersRoutes,
      transcriptionsRoutes,

      {
        name: RouteName.Forbidden,
        path: "/forbidden",
        component: () => import("@follaw-fr/views/ForbiddenView.vue"),
      },

      {
        name: RouteName.NotFound,
        path: "/:pathMatch(.*)*",
        component: () => import("@follaw-fr/views/ErrorView.vue"),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      // Use the saved position if available (for back/forward navigation)
      return savedPosition;
    } else if (to.hash) {
      // Scroll to the hash if it exists
      const element = document.querySelector(to.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
      return false;
    } else {
      // Scroll to the top of the page
      return { top: 0 };
    }
  },
});

/** GLOBAL GUARDS */

// Navigation guard to make sure the topbar and user are always loaded
// and redirect to onboarding if needed

router.beforeResolve(async (to) => {
  const { getUserAndFilters } = useUserStore();
  const { onboardingCompleted } = storeToRefs(useUserStore());
  const { isTokenValid } = storeToRefs(useAuthStore());
  const isInOnboarding = to.matched.some(
    (record) => record.name === "Onboarding"
  );

  if (!to.meta.isPublic && isTokenValid.value) {
    const { isTopbarReady } = storeToRefs(useUserStore());
    if (!isTopbarReady.value) {
      await getUserAndFilters();
    }

    const { user } = storeToRefs(useUserSettingsStore());
    if (!user.value) {
      const { getUserSettings } = useUserSettingsStore();
      await getUserSettings();
    }

    // If the user has not completed the onboarding, redirect to it
    if (!onboardingCompleted.value && !isInOnboarding) {
      return { name: "Onboarding" };
    }
  }
});

/**
 * GUARD: Auto Login
 */
router.beforeEach(async (to) => {
  const { createTokenWithDigicode } = useAuthStore();

  // Consume auto-login query parameters
  if (to.query.auto_login_email && to.query.auto_login_code) {
    await createTokenWithDigicode({
      user_email: to.query.auto_login_email as string,
      code: to.query.auto_login_code as string,
    });

    // Remove the auto-login query params
    delete to.query.auto_login_email;
    delete to.query.auto_login_code;
  }
});

/**
 * GUARD: Authentication
 */
router.beforeEach((to) => {
  const { isTokenValid } = storeToRefs(useAuthStore());

  // If the user is not authenticated and tries to access a private page
  // redirect the user to the login page
  if (!isTokenValid.value && !to.meta.isPublic) {
    // On garde la page demandée (lien d'un mail d'alerte, `?follaw=1` compris) pour y revenir après la connexion.
    return {
      name: RouteName.Login,
      query: {
        redirect: redirectionApresConnexion(cheminSansConnexionAutomatique(to)),
      },
    };
  }

  // If the user is authenticated and tries to access a public page, redirect to the dashboard
  if (isTokenValid.value && to.meta.isPublic) {
    return { name: RouteName.Root };
  }
});

/**
 * GUARD: Permission level
 * If the route requires a specific permission level (meta.permission level)
 * Then
 * If the user has the sufficient permission level -> routing pass
 * Else -> redirect to the lock page (meta.permissionLevel)
 */
router.beforeEach((to) => {
  // instead of having to check every route record with
  // to.matched.some(record => record.meta.requiresAuth)

  if (!to.meta.isPublic && to.meta.permissionLevel) {
    const { hasPermission } = useUserSettingsStore();
    if (!hasPermission(to.meta.permissionLevel)) {
      return to.meta.redirectPermissionDenied ?? { name: RouteName.Forbidden };
    }
  }
});

/** GUARD persistent query params */

const PERSISTENT_QUERY_PARAMS = [
  "dashboard",
  "search",
  "thematics",
  "subthematics",
];

router.beforeEach((to, from) => {
  const toast = useToast();

  // Consume Stripe status parameters
  // if the parameters are present, show the toast notification
  if (to.query["stripe-result"]) {
    if (to.query["stripe-result"] === "SUCCESS") {
      toast.success("Votre abonnement a été mis à jour avec succès !");
    } else if (to.query["stripe-result"] === "CANCEL") {
      toast.warning("Votre abonnement n'a pas été mis à jour.");
    }

    delete to.query["stripe-result"];
  }

  // Extract the persistent params from the `from` route
  const persistedParams: Record<
    string,
    LocationQueryValue | LocationQueryValue[]
  > = {};
  for (const key of PERSISTENT_QUERY_PARAMS) {
    if (from.query[key] !== undefined && to.query[key] === undefined) {
      // Only persist if it existed before and is not being set in the new route
      persistedParams[key] = from.query[key];
    }
  }
  // If the path changes or the targeted path has no dashboard then redirect with the persisted params
  if (
    Object.keys(persistedParams).length > 0 &&
    (from.path !== to.path || !to.query.dashboard)
  ) {
    return {
      ...to,
      query: {
        ...persistedParams,
        ...to.query,
      },
    };
  }
});

export default router;
